import TipsAndUpdatesRounded from '@mui/icons-material/TipsAndUpdatesRounded';
import Box from '@mui/material/Box';

import { type NudgePulse, nudgePulseSx } from '@/components/atoms';

type Props = {
  /** Diameter in px. `TIP_LAUNCHER_SIZE` standing alone, 32 inside a surface. */
  size: number;
  /** Which cadence to ping on, or `false` for a glyph at rest. */
  pulse?: NudgePulse | false;
};

/**
 * The lightbulb, on a brand-red disc. The one mark the whole feature is
 * recognised by, and the thing the pulse radiates from.
 *
 * It appears at three sizes and in three places — alone as the launcher, in
 * the corner of the phone's collapsed pill, and at the head of the open card —
 * and it is deliberately identical in all three. That repetition is what lets
 * a visitor read the disc they tapped and the card that opened as the same
 * object: there is no other shared visual cue between a 48px circle and a
 * 360px card.
 *
 * ## Why it is always filled, never tinted
 *
 * The obvious variant is a soft `primary` tint behind a `primary` icon inside
 * the card, where a saturated disc is louder than it needs to be. It is also
 * the one combination this palette cannot have: a brand-red glyph on a 12%
 * brand-red wash on paper is the contrast trap the services page already
 * walked into, and an icon carrying meaning owes 3:1 like any other non-text
 * content. `primary.main` with `primary.contrastText` on it is the pairing the
 * theme guarantees in both schemes, so that is the only one used.
 *
 * ## Decorative, in every position
 *
 * `aria-hidden` throughout, and no label of its own. Everywhere the glyph
 * appears it sits inside something that already has a name — the launcher's
 * `aria-label`, the pill's own visible text, the card's heading — so
 * announcing it again would only mean hearing "tip" twice before the tip.
 */
export function TipGlyph({ size, pulse = false }: Props) {
  return (
    <Box
      aria-hidden
      sx={{
        // The ring on `::after` is positioned against this, and `inherit` on
        // its radius is what keeps it a circle without being told the size.
        position: 'relative',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: 999,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        ...nudgePulseSx(pulse),
      }}
    >
      {/* Half the disc is the optical centre for a glyph in a circle: the
          lightbulb's own artwork carries padding, so matching the disc any
          more closely reads as a bulb squeezed into it. Nothing is needed to
          keep it clear of the pulse — the ring has a transparent centre and
          paints only at its own rim, which starts at the disc's edge. */}
      <TipsAndUpdatesRounded sx={{ fontSize: size * 0.5 }} />
    </Box>
  );
}
